// Log Review Time — On-Track's own view of the "Log Review Time" field on
// the work item screen (FIELD_VIEWS in client-custom/frontend/client.config.js),
// drawn in the Details panel of a Draughting Sub-task (client, 2026-10-20).
//
// A small card in the manner of the Purchase Order Lines document: it names
// whom the time is logged to — "Logs to: Assigned Resource = <the option
// matching the Requested Approver>" — or says the approver has no such
// option (an admin adds or renames it); a box for the hours; "Log Time"
// creates "REVIEW TIME LOG: <summary>" beside the work item, carrying what
// its create screen needs, and moves it to Done; the logs already made are
// listed under it. The rules are in ./logReviewTime.js.
//
// Props (the FIELD_VIEWS contract): { issueKey, fieldId, fieldName, value, editable, onFieldsChanged }.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { client } from '@platform/http/api-client';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import {
  LOG_REVIEW, buildLog, defaultLogger, fieldIdOf, initialStatusOf, kindOf, logsOf, matchApprovers, normName, openStep, parseHours, pathTo, peopleIn, todayISO,
} from './logReviewTime';

// The workflow's own reasons first ("Transition blocked" says nothing without them).
const errText = (e) => {
  const d = e?.response?.data || {};
  const reasons = Array.isArray(d.errorMessages) && d.errorMessages.length ? d.errorMessages.join(' · ') : '';
  const errs = d.errors && typeof d.errors === 'object' ? Object.values(d.errors).join(' · ') : '';
  return [d.error, reasons || errs || d.details].filter(Boolean).join(' — ') || e?.message || 'failed';
};
// The site's field list (~160 KB) once per page, not once per work item opened.
let fieldsCache = null;
const siteFields = () => {
  if (!fieldsCache || Date.now() - fieldsCache.at > 5 * 60 * 1000) {
    fieldsCache = { at: Date.now(), list: client.get('/api/fields').then((r) => (Array.isArray(r.data) ? r.data : [])).catch((e) => { fieldsCache = null; throw e; }) };
  }
  return fieldsCache.list;
};
const fmtH = (n) => (Number.isFinite(n) ? `${n.toLocaleString('en-US', { maximumFractionDigits: 2 })} h` : '—');

const S = {
  card: { border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface, var(--bg-primary))', overflow: 'hidden', fontSize: 13, color: 'var(--text-primary)' },
  head: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-secondary)' },
  body: { padding: '9px 10px', display: 'flex', flexDirection: 'column', gap: 8 },
  label: { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.02em' },
  chip: (on, ok) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: ok ? 'pointer' : 'default', border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`, background: on ? 'color-mix(in srgb, var(--accent) 14%, transparent)' : 'transparent', color: ok ? 'var(--text-primary)' : 'var(--text-secondary)' }),
  warn: { padding: '7px 9px', borderRadius: 8, background: 'var(--warning-soft)', fontSize: 12.5, lineHeight: 1.4 },
  bad: { padding: '7px 9px', borderRadius: 8, background: 'var(--danger-soft)', color: 'var(--danger)', fontSize: 12.5, lineHeight: 1.4, fontWeight: 600 },
  good: { padding: '7px 9px', borderRadius: 8, background: 'var(--success-soft)', fontSize: 12.5, lineHeight: 1.4, fontWeight: 600 },
  input: { width: 88, padding: '6px 8px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', font: 'inherit', fontSize: 13, textAlign: 'right' },
  btn: (on) => ({ padding: '6px 14px', borderRadius: 8, border: 'none', background: on ? 'var(--accent)' : 'var(--bg-tertiary)', color: on ? '#fff' : 'var(--text-secondary)', font: 'inherit', fontSize: 13, fontWeight: 800, cursor: on ? 'pointer' : 'default', boxShadow: on ? '0 1px 2px rgba(0,0,0,.15)' : 'none', whiteSpace: 'nowrap' }),
  row: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  log: { display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12, padding: '3px 0', borderTop: '1px solid var(--border)' },
  key: { color: 'var(--accent)', fontWeight: 700, textDecoration: 'none' },
};

function ClockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
    </svg>
  );
}

export default function LogReviewTime({ issueKey }) {
  const { user } = useAuth();
  const [ctx, setCtx] = useState(null);          // { source, ids, kinds, options, fieldList } | { error }
  const [siblings, setSiblings] = useState(null);
  const [pick, setPick] = useState(-1);          // which approver the time is logged to
  const [hoursText, setHoursText] = useState('');
  const [busy, setBusy] = useState('');          // '' | what is being done
  const [result, setResult] = useState(null);    // { ok, key, note } | { error, key? }
  const [tick, setTick] = useState(0);

  // the work item, the site's fields, the Assigned Resource's options — and,
  // in the background, what Log Time needs (the create screen, the workflow),
  // so the click does not wait for them (client, 2026-10-20: "very slow")
  useEffect(() => {
    let on = true;
    setCtx(null);
    (async () => {
      try {
        const [issue, fieldList] = await Promise.all([
          client.get(`/api/issue/${encodeURIComponent(issueKey)}`, { params: { fields: '*all' } }).then((r) => r.data),
          siteFields(),
        ]);
        const sf = issue?.fields || {};
        const projectKey = sf.project?.key || String(issue?.key || issueKey).split('-')[0];
        const prep = {
          meta: client.get(`/api/createmeta/${encodeURIComponent(projectKey)}/${encodeURIComponent(sf.issuetype?.id)}`).then((r) => r.data || {}).catch(() => ({})),
          wf: client.get(`/api/jira/issue-workflow/${encodeURIComponent(sf.project?.id || projectKey)}/${encodeURIComponent(sf.issuetype?.id)}`).then((r) => r.data).catch(() => null),
        };
        const ids = {
          approvers: fieldIdOf(LOG_REVIEW.approversField, fieldList),
          assigned: fieldIdOf(LOG_REVIEW.assignedField, fieldList),
          est: fieldIdOf(LOG_REVIEW.estField, fieldList),
          act: fieldIdOf(LOG_REVIEW.actField, fieldList),
          start: fieldIdOf(LOG_REVIEW.startField, fieldList),
          completed: fieldIdOf(LOG_REVIEW.completedField, fieldList),
          carry: LOG_REVIEW.carry.map((n) => fieldIdOf(n, fieldList)).filter(Boolean),
          fixed: LOG_REVIEW.fixed.map((x) => ({ id: fieldIdOf(x.field, fieldList), option: x.option })).filter((x) => x.id),
        };
        const assignedField = fieldList.find((f) => f && (f.id === ids.assigned || f.key === ids.assigned));
        const assignedKind = kindOf(assignedField);
        let options = [];
        if (ids.assigned && assignedKind !== 'user' && assignedKind !== 'users') {
          const o = await client.get('/api/field-options', { params: { fieldId: ids.assigned, issueKey } }).then((r) => r.data).catch(() => []);
          options = (Array.isArray(o) ? o : (o?.values || o?.options || [])).map((x) => (typeof x === 'string' ? x : (x.value || x.name))).filter(Boolean);
        }
        if (on) setCtx({ source: issue, ids, assignedKind, options, prep });
      } catch (e) { if (on) setCtx({ error: errText(e) }); }
    })();
    return () => { on = false; };
  }, [issueKey, tick]);

  // the logs already made: siblings named "REVIEW TIME LOG: <summary>"
  const parentKey = ctx?.source?.fields?.parent?.key || null;
  useEffect(() => {
    if (!ctx || ctx.error) return undefined;
    let on = true;
    const f = ['summary', 'status', 'created', ctx.ids.assigned, ctx.ids.act].filter(Boolean).join(',');
    const jql = parentKey ? `parent = ${parentKey} order by created desc` : null;
    if (!jql) { setSiblings([]); return undefined; }
    client.post('/api/search', { jql, fields: f, maxResults: 300, skipTotal: true })
      .then((r) => { if (on) setSiblings(r.data?.issues || []); })
      .catch(() => { if (on) setSiblings([]); });
    return () => { on = false; };
  }, [ctx, parentKey]);

  const approvers = useMemo(() => (ctx && !ctx.error && ctx.ids.approvers ? peopleIn(ctx.source?.fields?.[ctx.ids.approvers]) : []), [ctx]);
  const matches = useMemo(() => (ctx && !ctx.error ? matchApprovers(approvers, ctx.options, ctx.assignedKind) : []), [ctx, approvers]);
  useEffect(() => { setPick(defaultLogger(matches, user)); }, [matches, user]);
  const logs = useMemo(() => (ctx && !ctx.error && siblings ? logsOf(siblings, ctx.source, ctx.ids) : []), [ctx, siblings]);
  const hours = parseHours(hoursText);
  const match = pick >= 0 ? matches[pick] : null;
  const canLog = !!(match && match.option && hours && !busy);

  // THE LOG IN AS FEW ROUND TRIPS AS IT CAN (client, 2026-10-20: "very slow" —
  // each request to the site costs ~0.5 s, and the first version made 13 in a
  // row). The create screen and the workflow were read when the card loaded;
  // the new item starts in the workflow's initial status, so it is not read
  // back; each step POSTs the planned transition straight away (the open
  // transitions are read only when one is refused as unknown — and when none
  // leads on, the way is planned again from where the item really is); the
  // fields the create screen does not carry (the actual hours, the
  // correctness check — only the LAST step needs them) are written while the
  // first steps run.
  const logTime = useCallback(async () => {
    if (!canLog) return;
    const { source, ids, assignedKind, prep } = ctx;
    const t0 = Date.now();
    let newKey = null;
    setResult(null);
    try {
      setBusy('Creating the log…');
      const [meta, wf] = await Promise.all([prep.meta, prep.wf]);
      const { fields, later } = buildLog({ source, meta, ids, match, assignedKind, hours, today: todayISO() });
      const created = await client.post('/api/issue', { fields }).then((r) => r.data);
      newKey = created?.key;
      if (!newKey) throw new Error('the create returned no key');
      const key = encodeURIComponent(newKey);
      const notes = [];
      const laterDone = Object.keys(later).length
        ? client.put(`/api/issue/${key}`, { fields: later }).catch((e) => { notes.push(`some fields could not be set (${errText(e)})`); })
        : Promise.resolve();
      const trs = wf?.transitions || [];
      const statusNow = async () => (await client.get(`/api/issue/${key}`, { params: { fields: 'status' } })).data?.fields?.status?.name;
      let at = initialStatusOf(trs) || await statusNow();
      let steps = pathTo(trs, at, LOG_REVIEW.doneStatus);
      const total = steps ? steps.length : 0;
      let n = 0;
      while (steps && steps.length && n < 12) {
        const step = steps[0];
        if (steps.length === 1) await laterDone;          // the last step's validators read them
        n += 1;
        setBusy(`Moving it to ${LOG_REVIEW.doneStatus} — step ${Math.min(n, total)} of ${total}…`);
        try {
          // eslint-disable-next-line no-await-in-loop
          await client.post(`/api/issue/${key}/transition`, { transitionId: step.id });
          at = step.to; steps = steps.slice(1);
        } catch (e) {
          if (e?.response?.status !== 404) throw e;       // refused by a validator or a condition: say why
          // eslint-disable-next-line no-await-in-loop
          const t = openStep((await client.get(`/api/issue/${key}/transitions`)).data, step);
          if (t) {
            // eslint-disable-next-line no-await-in-loop
            await client.post(`/api/issue/${key}/transition`, { transitionId: String(t.id) });
            at = step.to; steps = steps.slice(1);
          } else {
            // eslint-disable-next-line no-await-in-loop
            at = await statusNow();
            steps = pathTo(trs, at, LOG_REVIEW.doneStatus);
            if (steps && steps.length && steps[0].id === step.id) throw new Error(`"${step.name}" (to ${step.to}) is not open to you on ${newKey} — the workflow keeps that step to certain groups; someone who may take it can finish the log`);
          }
        }
      }
      if (steps == null) throw new Error(`the workflow has no way from "${at || '?'}" to "${LOG_REVIEW.doneStatus}"`);
      if (steps.length) throw new Error(`${newKey} stopped in "${at}" on its way to ${LOG_REVIEW.doneStatus}`);
      await laterDone;
      setResult({ ok: true, key: newKey, secs: ((Date.now() - t0) / 1000).toFixed(1), note: notes.join(' · ') });
      setHoursText('');
      setSiblings((s) => [{ key: newKey, fields: { summary: fields.summary, status: { name: LOG_REVIEW.doneStatus }, created: new Date().toISOString(), ...(ids.assigned ? { [ids.assigned]: [{ value: match.option, displayName: match.name }] } : {}), ...(ids.act ? { [ids.act]: hours } : {}) } }, ...(s || [])]);
    } catch (e) {
      setResult({ error: errText(e), key: newKey });
    } finally { setBusy(''); }
  }, [canLog, ctx, match, hours]);

  if (!ctx) return <div style={{ ...S.card, padding: '8px 10px', color: 'var(--text-secondary)', fontSize: 12.5 }}>Reading the review…</div>;
  if (ctx.error) return <div style={S.bad}>Log Review Time could not read {issueKey}: {ctx.error} <button type="button" onClick={() => setTick((t) => t + 1)} style={{ ...S.btn(false), padding: '2px 8px', marginLeft: 6 }}>Retry</button></div>;

  const missing = [!ctx.ids.approvers && LOG_REVIEW.approversField, !ctx.ids.assigned && LOG_REVIEW.assignedField].filter(Boolean);
  const total = logs.reduce((a, l) => a + (Number.isFinite(l.hours) ? l.hours : 0), 0);
  return (
    <div style={S.card}>
      <div style={S.head}><ClockIcon /> Review time{logs.length > 0 && <span style={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0, fontWeight: 700 }}>{fmtH(total)} logged</span>}</div>
      <div style={S.body}>
        {missing.length > 0 && <div style={S.bad}>This site has no field named {missing.map((m) => `“${m}”`).join(' or ')} — an admin must add it before review time can be logged.</div>}
        {!missing.length && !approvers.length && <div style={S.warn}>No <strong>{LOG_REVIEW.approversField}</strong> on this work item yet — name the reviewer there, and the time logs to them.</div>}
        {!missing.length && approvers.length > 0 && (
          <div>
            <div style={S.label}>Logs to: {LOG_REVIEW.assignedField}{'\u00a0'}=</div>
            <div style={{ ...S.row, marginTop: 4 }}>
              {matches.map((m, i) => (
                <span key={`${m.name}-${i}`} role="button" tabIndex={m.option ? 0 : -1} onClick={() => m.option && setPick(i)} onKeyDown={(e) => { if (m.option && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setPick(i); } }}
                  title={m.option ? (matches.length > 1 ? `Log the review time to ${m.option}` : m.option) : `“${m.name}” is not an option of ${LOG_REVIEW.assignedField}`}
                  style={S.chip(i === pick, !!m.option)}>
                  {m.option ? (i === pick ? '✓ ' : '') : '⚠ '}{m.option || m.name}
                </span>
              ))}
            </div>
            {matches.some((m) => !m.option) && (
              <div style={{ ...S.warn, marginTop: 6 }}>
                <strong>Mismatch:</strong> {matches.filter((m) => !m.option).map((m) => `“${m.name}”`).join(', ')} {matches.filter((m) => !m.option).length === 1 ? 'is' : 'are'} a {LOG_REVIEW.approversField.replace(/s$/, '')} but not an option of <strong>{LOG_REVIEW.assignedField}</strong>. An admin must add or edit the option (Settings → Fields → {LOG_REVIEW.assignedField}) with the same name before the time can be logged to {matches.filter((m) => !m.option).length === 1 ? 'them' : 'those people'}.
              </div>
            )}
          </div>
        )}
        <div style={S.row}>
          <label style={{ ...S.row, gap: 6 }}>
            <span style={S.label}>Hours</span>
            <input type="number" inputMode="decimal" min="0.25" step="0.25" value={hoursText} placeholder="0.0" disabled={!!busy}
              onChange={(e) => { setHoursText(e.target.value); setResult(null); }} onKeyDown={(e) => { if (e.key === 'Enter') logTime(); }} style={S.input} aria-label="Review hours" />
          </label>
          <button type="button" onClick={logTime} disabled={!canLog} style={S.btn(canLog)}
            title={canLog ? `Create “${LOG_REVIEW.summaryPrefix}${ctx.source.fields?.summary || ''}” for ${match.option}, ${fmtH(hours)}, and move it to ${LOG_REVIEW.doneStatus}` : !match?.option ? 'Nobody to log the time to — see above' : 'Type the hours the review took'}>
            {busy ? 'Logging…' : 'Log Time'}
          </button>
        </div>
        {busy && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{busy}</div>}
        {result?.ok && <div style={S.good}>✓ Logged — <a href={`/browse/${result.key}`} target="_blank" rel="noreferrer" style={S.key}>{result.key}</a> is {LOG_REVIEW.doneStatus}{result.secs ? <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> · {result.secs} s</span> : null}.{result.note ? <div style={{ fontWeight: 400, marginTop: 3 }}>{result.note}</div> : null}</div>}
        {result?.error && <div style={S.bad}>{result.key ? <>Created <a href={`/browse/${result.key}`} target="_blank" rel="noreferrer" style={S.key}>{result.key}</a>, but it could not be finished: </> : 'The time could not be logged: '}{result.error}</div>}
        {logs.length > 0 && (
          <div>
            {logs.slice(0, 8).map((l) => (
              <div key={l.key} style={S.log}>
                <a href={`/browse/${l.key}`} target="_blank" rel="noreferrer" style={S.key}>{l.key}</a>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{l.who || '—'}{l.created ? ` · ${l.created.split('-').reverse().join('/')}` : ''}{normName(l.status) !== normName(LOG_REVIEW.doneStatus) ? ` · ${l.status}` : ''}</span>
                <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtH(l.hours)}</strong>
              </div>
            ))}
            {logs.length > 8 && <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', paddingTop: 2 }}>and {logs.length - 8} more</div>}
          </div>
        )}
      </div>
    </div>
  );
}
